import React, { useEffect, useMemo } from 'react'

import {
  alpha,
  Box,
  IconButton,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { format, isValid, parseISO } from 'date-fns'
import { de, enUS, es, fr, it, uk } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

import Iconify from '../../components/Iconify/Iconify'
import { CommonTableColumnsEnum } from '../../components/Table/types'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useThemeMode } from '../../theme/ThemeModeContext'
import { SortDirection, SortDirectionCapital } from '../../types'
import { currencyFormatter, getTablePaginationDisplayedRows } from '../../utils'
import { useIsMobile } from '../../utils/useIsMobile'
import { EmptyData } from '../Reports'

import {
  fetchCashFlows,
  resetCashFlowView,
  updateCashFlowOrderBy,
  updateCashFlowPage,
  updateCashFlowPageSize,
  updateCashFlowSortOrder,
} from './CashFlowSlice'
import { CashFlowEntry } from './types'

const columns: (keyof CashFlowEntry)[] = [
  CommonTableColumnsEnum.amount,
  CommonTableColumnsEnum.cashFlowType,
  CommonTableColumnsEnum.comment,
  CommonTableColumnsEnum.date,
]

export const CashFlowTable: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t, i18n } = useTranslation()
  const { spacing, palette } = useTheme()
  const { resolvedMode } = useThemeMode()
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile('md')

  const {
    rows,
    pagination: { page, perPage, itemCount, order, orderBy },
  } = useAppSelector((state) => state.cashFlow)
  const { currentAccount, period } = useAppSelector((state) => state.userAccounts)

  const [startDate, endDate] = period
  const searchPeriod = useMemo(
    () => (startDate && endDate ? [startDate, endDate] : [null, null]),
    [startDate, endDate],
  )

  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
  const dateLocale = { en: enUS, uk, es, fr, it, de }[language] || enUS
  const headerColor =
    resolvedMode === 'dark' ? alpha(palette.background.default, 0.5) : palette.blue[100]
  const hoverColor =
    resolvedMode === 'dark'
      ? alpha(palette.background.paper, 0.95)
      : alpha(palette.blue[500] as unknown as string, 0.08)

  useEffect(() => {
    dispatch(resetCashFlowView())
  }, [dispatch])

  useEffect(() => {
    if (currentAccount?.id) {
      dispatch(
        fetchCashFlows({
          order,
          orderBy: orderBy || CommonTableColumnsEnum.date,
          page,
          perPage,
          period: searchPeriod,
          account: currentAccount.id,
        }),
      )
    }
  }, [page, perPage, searchPeriod, currentAccount?.id, order, orderBy, dispatch])

  const handlePageChange = (_e: unknown, newPage: number) => {
    dispatch(updateCashFlowPage(newPage + 1))
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateCashFlowPageSize(parseInt(e.target.value, 10)))
    dispatch(updateCashFlowPage(1))
  }

  const handleSort = (sortColumn: keyof CashFlowEntry) => {
    if (sortColumn === CommonTableColumnsEnum.comment) {
      return
    }

    if (orderBy === sortColumn) {
      dispatch(
        updateCashFlowSortOrder(
          order === SortDirectionCapital.asc ? SortDirectionCapital.desc : SortDirectionCapital.asc,
        ),
      )
    } else {
      dispatch(updateCashFlowOrderBy(sortColumn))
    }
  }

  const renderCell = (column: keyof CashFlowEntry, row: CashFlowEntry) => {
    if (column === CommonTableColumnsEnum.amount) {
      const amount = Number(row[column])
      const color =
        amount < 0 ? palette.red[400] : amount > 0 ? palette.green[400] : palette.text.primary

      return (
        <Typography fontWeight={700} color={color}>
          {currencyFormatter(currentAccount?.currency, amount)}
        </Typography>
      )
    }

    if (column === CommonTableColumnsEnum.cashFlowType) {
      return t(`cashFlowPage.type.${String(row[column]).toLowerCase()}`, String(row[column]))
    }

    if (column === CommonTableColumnsEnum.comment) {
      if (embedded) {
        return row[column] ? (
          <Tooltip title={row[column]} placement="top">
            <IconButton size="small" sx={{ p: 0 }}>
              <Iconify icon="material-symbols:chat-outline" width={18} height={18} />
            </IconButton>
          </Tooltip>
        ) : (
          '—'
        )
      }

      return row[column] || '—'
    }

    if (column === CommonTableColumnsEnum.date) {
      const date = parseISO(String(row[column]))

      return isValid(date)
        ? format(date, 'HH:mm dd MMM yyyy', { locale: dateLocale })
        : String(row[column] || '—')
    }

    return row[column]
  }

  if (!rows.length) {
    return (
      <Box mt={4} height={embedded ? spacing(30) : '100%'}>
        <EmptyData
          title={t('cashFlowPage.emptyState.title', 'No cash flows yet')}
          subtitle={t(
            'cashFlowPage.emptyState.message',
            'Deposits, withdrawals, credits, and balance adjustments will appear here when available.',
          )}
        />
      </Box>
    )
  }

  return (
    <>
      <TableContainer sx={{ flexGrow: 1 }}>
        <MuiTable>
          <TableHead>
            <TableRow sx={{ backgroundColor: headerColor }}>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    color: palette.grey[700],
                    fontWeight: 800,
                    fontSize: isMobile ? 12 : 14,
                    textAlign: 'left',
                    padding: spacing(1.125),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column === CommonTableColumnsEnum.comment ? (
                    t(column)
                  ) : (
                    <TableSortLabel
                      active={orderBy === column}
                      direction={order.toLowerCase() as SortDirection}
                      onClick={() => handleSort(column)}
                      sx={{ flexDirection: 'row' }}
                    >
                      {t(column)}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  bgcolor: palette.background.paper,
                  '&:hover': {
                    bgcolor: hoverColor,
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      fontSize: { xs: 12, md: 14 },
                      fontWeight: 500,
                      textAlign: 'left',
                      padding: spacing(1.25),
                      textTransform:
                        column === CommonTableColumnsEnum.comment ? 'none' : 'capitalize',
                      '& div': {
                        justifyContent: 'left',
                      },
                    }}
                  >
                    <Box display="flex" flexDirection="row">
                      {renderCell(column, row) || '—'}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        labelRowsPerPage={t('perPage', 'Per page')}
        labelDisplayedRows={getTablePaginationDisplayedRows(t)}
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        component="div"
        count={itemCount}
        rowsPerPage={perPage}
        page={page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />
    </>
  )
}
